import {Box, CircularProgress, IconButton, TextField, Typography} from "@mui/material";
import React, {useCallback, useEffect, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {NexusSearchSchemaTypes} from "../../../../../../../core/constants/enums";
import Api from "../../../../../../../core/services/api/api";
import {ReactComponent as SaveIcon} from "../../../../../../../assets/images/save-rollback/check.svg";
import {ReactComponent as CancelIcon} from "../../../../../../../assets/images/save-rollback/cancel.svg";
import './index.scss';
import {FingerprintRounded, KeyboardReturnRounded} from "@mui/icons-material";

/**
 * Returns true when the loaded schema belongs to the given industry.
 * Schema entries include industryId from getNexusSearchBaseStructure.
 */
const schemaMatchesIndustry = (searchSchema, industryId) => {
    if (!industryId || !searchSchema?.entries?.length) {
        return false;
    }
    const schemaIndustryId = searchSchema.entries.find((entry) => entry.industryId != null)?.industryId;
    if (schemaIndustryId == null) {
        // Some schemas may omit industryId; allow apply only when entries exist.
        return true;
    }
    return String(schemaIndustryId) === String(industryId);
};

const LookupSearchVinDecoder = ({
                                    searchSchema,
                                    applyFilters,
                                    readonly,
                                    initialVin = '',
                                    industryId,
                                }) => {
    const [vin, setVin] = useState(() => initialVin?.trim()?.toUpperCase() ?? '');
    const [response, setResponse] = useState([]);
    const isMounted = useIsMounted();
    /** Tracks `${industryId}:${vin}` so the same VIN re-runs after an industry switch. */
    const autoSubmittedKey = useRef('');
    const industryIdRef = useRef(industryId);
    industryIdRef.current = industryId;

    /**
     * Handle VIN input change
     * @type {function(*): void}
     */
    const onVinChanged = useCallback((event) => setVin(event.target.value?.toUpperCase()), []);

    /**
     * Fetch applicable filters based on the decoded VIN data
     * @type {function(*): Promise<*[]>}
     */
    const fetchApplicableFilters = useCallback(async (data, forIndustryId) => {
        return null; // TODO: remove this return statement when we want to show applicable filters based on the decoded VIN data

        setResponse(prevState => [
            ...prevState.slice(0, 1),
            {status: "pending", message: "Fetching applicable filters..."},
        ]);

        const nonNullData = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== null && value !== undefined));

        const propertyGroupsSchemaEntries = searchSchema.entries?.filter(entry => entry.type === NexusSearchSchemaTypes.propertyGroup && entry.parentId !== null);
        const propertyGroupParentId = searchSchema.entries?.find(entry => entry.type === NexusSearchSchemaTypes.propertyGroup && entry.parentId === null);

        if (!propertyGroupParentId || !propertyGroupsSchemaEntries?.length) {
            setResponse(prevState => [
                ...prevState.slice(0, 1),
                {status: 'error', message: "No applicable filters found for the decoded VIN"},
            ]);
            return [];
        }

        const searchQuery = {};
        const foundFilters = [];

        // for each application schema entry, we do an API call to fetch all the data, then find the lowercased value to match against the data
        for (const schemaEntry of propertyGroupsSchemaEntries) {
            if (String(industryIdRef.current ?? '') !== String(forIndustryId ?? '')) {
                return null;
            }
            const key = schemaEntry.title.slice(0, 1).toLowerCase() + schemaEntry.title.slice(1);
            if (!nonNullData[key]) {
                continue; // Skip if the data for this schema entry is not present
            }
            const response = await Api.queryIndustryGroupProperties({
                industryGroupPropertyId: propertyGroupParentId.originalId,
                returnAttrId: schemaEntry.originalId,
                searchQuery: searchQuery,
            })
            if (!isMounted())
                return null;
            if (String(industryIdRef.current ?? '') !== String(forIndustryId ?? '')) {
                return null;
            }
            if (response.resultFlag) {
                const foundValue = response.data?.find(item => item.value?.toString()?.toLowerCase() === nonNullData[key].toString()?.toLowerCase());
                if (foundValue) {
                    searchQuery[schemaEntry.title] = [foundValue.value];
                    foundFilters.push({
                        filterId: schemaEntry.id,
                        value: foundValue.value?.toString(),
                    });
                }
            }
        }
        setResponse(prevState => [
            ...prevState.slice(0, 1),
            ...(foundFilters.length === propertyGroupsSchemaEntries.length ? [{
                status: 'success',
                message: "Applied all applicable filter s from the decoded VIN.",
            }] : []),
            ...(foundFilters.length === 0 ? [{
                status: 'error',
                message: "No applicable filters found for the decoded VIN",
            }] : []),
            ...(foundFilters.length > 0 && foundFilters.length < propertyGroupsSchemaEntries.length ? [{
                status: 'warning',
                message: `Applied ${foundFilters.length} / ${propertyGroupsSchemaEntries.length} filters from the decoded VIN.`,
            }] : []),
        ]);
        return foundFilters;
    }, [searchSchema, isMounted]);

    /**
     * Handle VIN submission
     * @type {(function(): Promise<void>)}
     */
    const onVinSubmitted = useCallback(async (overrideVin) => {
        const vinNumber = (overrideVin)?.trim() ?? "";
        const forIndustryId = industryId;
        if (!vinNumber) {
            setResponse([{status: "error", message: "VIN number cannot be empty."}]);
            return;
        }
        if (!forIndustryId || !schemaMatchesIndustry(searchSchema, forIndustryId)) {
            return;
        }
        setResponse([{status: "pending", message: "Decoding VIN..."}]);
        const response = await Api.decodeVin(vinNumber);
        if (!isMounted())
            return;
        if (String(industryIdRef.current ?? '') !== String(forIndustryId)) {
            return;
        }
        if (response.resultFlag && Object.keys(response.data).length > 0) {
            setResponse([{status: "success", message: ["VIN decoded successfully."]}]);
            // Object.entries(response.data ?? {}).map(e => e[1]).filter(Boolean).join(', ')
            const fistLineResult = [response.data?.make, response.data?.model].filter(Boolean).join(', ') + (response.data?.year ? ` (${response.data?.year})` : "");
            const secondLineResult = [response.data?.tier, response.data?.bodyClass].filter(Boolean).join(', ');
            setResponse(prevState => [
                ...prevState.slice(0, 1).map(item => ({
                    ...item,
                    message: [
                        ...item.message,
                        `<b style="font-size: 14px">${fistLineResult}</b> <br/> <b style="font-size: 12px">${secondLineResult}</b>`,
                    ]
                })),
            ]);
            const filters = await fetchApplicableFilters(response.data, forIndustryId);
            if (!isMounted())
                return;
            if (filters == null || String(industryIdRef.current ?? '') !== String(forIndustryId)) {
                return;
            }
            applyFilters(filters);
        } else {
            setResponse([{status: "error", message: response?.message ?? "VIN decoding failed."}]);
        }
    }, [applyFilters, fetchApplicableFilters, industryId, isMounted, searchSchema]);

    /**
     * Effect to auto-submit the initial VIN once the schema for this industry is ready.
     */
    useEffect(() => {
        const normalizedInitialVin = initialVin?.trim()?.toUpperCase() ?? '';
        if (!normalizedInitialVin || readonly || !industryId) {
            return;
        }
        if (!schemaMatchesIndustry(searchSchema, industryId)) {
            return;
        }
        const submitKey = `${industryId}:${normalizedInitialVin}`;
        if (autoSubmittedKey.current === submitKey) {
            return;
        }
        autoSubmittedKey.current = submitKey;
        setVin(normalizedInitialVin);
        void onVinSubmitted(normalizedInitialVin);
    }, [initialVin, industryId, onVinSubmitted, readonly, searchSchema]);

    return (
        <Box className={'vin-decoder'} sx={{display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%'}}>
            <TextField
                name={'vin-number'}
                label={
                    <Typography sx={{margin: 0, padding: 0}} variant={'label'}>
                        VIN Decoder <Typography variant={'label'} component={'span'} color={'error'} fontWeight={600} marginInlineStart={0.5}>BETA</Typography>
                    </Typography>
                }
                value={vin}
                onChange={onVinChanged}
                disabled={readonly}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        void onVinSubmitted(e.target.value);
                    }
                }}
                placeholder={'Enter VIN number'}
                variant={'outlined'}
                fullWidth
                autoComplete={'off'}
                InputProps={{
                    startAdornment: <FingerprintRounded sx={{marginInlineEnd: 1}}/>,
                    endAdornment: (
                        !!vin &&
                        <IconButton color={'primary'} className={'p-0'} onClick={() => void onVinSubmitted(vin)} component={'span'}>
                            <KeyboardReturnRounded tabIndex={-1}/>
                        </IconButton>
                    )
                }}
            />
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, width: '100%'}}>
                {
                    response.map((item) =>
                        <Box key={item.status + item.message} sx={{display: 'flex', width: '100%', justifyContent: 'flex-start', gap: 1}}>
                            {
                                item.status === 'pending' &&
                                <CircularProgress size={18}/>
                            }
                            {
                                item.status === 'success' &&
                                <SaveIcon className={'success-icon'} style={{minWidth: 24, height: 24}}/>
                            }
                            {
                                item.status === 'error' &&
                                <CancelIcon className={'error-icon'} style={{minWidth: 24, height: 24}}/>
                            }
                            {
                                item.status === 'warning' &&
                                <SaveIcon className={'warning-icon'} style={{minWidth: 24, height: 24}}/>
                            }
                            {
                                item.message &&
                                <Box sx={{display: 'flex', flexDirection: 'column',}}>
                                    {(typeof item.message === 'string' ? [item.message] : item.message).map((msg) => (
                                        <Typography key={msg} variant={'caption'} dangerouslySetInnerHTML={{__html: msg}}/>
                                    ))}
                                </Box>
                            }
                        </Box>
                    )
                }
            </Box>
        </Box>
    );
}

export default LookupSearchVinDecoder;
